// Summary of the user's loon balance and actions.

import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
  ChevronRightIcon,
  ChevronsUpDownIcon,
  CoinsIcon,
  ExternalLinkIcon,
  HistoryIcon,
  ZapIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import AnimatedOdometerNumber from '@/components/ui/custom/AnimatedOdometerNumber';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/utilities/cn';
import { useUserStripePortal } from '@/hooks';
import { analyticsService } from '@/services/analyticsService';
import { useLoonProducts } from '@/transport/cache';
import { ASSETS } from '@airhead-games/shared/configs';
import { getProductLoonsGranted } from '@airhead-games/shared/utilities';
import type { UserAuthFields } from '@airhead-games/shared/types';
import type { LibraryDialog } from '../libraryRouteTypes';

type LoonSummaryProps = {
  user: UserAuthFields;
  openDialog: (dialog: LibraryDialog) => void;
};

/** Match sidebar nav rows; override DropdownMenuItem's `**:text-accent-foreground` (icons + text). */
const librarySidebarDropdownItemClassName =
  'focus:bg-sidebar-accent focus:text-sidebar-accent-foreground data-[highlighted]:bg-sidebar-accent data-[highlighted]:text-sidebar-accent-foreground not-data-[variant=destructive]:focus:**:text-sidebar-accent-foreground not-data-[variant=destructive]:data-[highlighted]:**:text-sidebar-accent-foreground';

const LoonSummary: React.FC<LoonSummaryProps> = ({ user, openDialog }) => {
  const location = useLocation();
  const { isMobile, state } = useSidebar();
  const collapsedDesktop = !isMobile && state === 'collapsed';
  const [menuOpen, setMenuOpen] = useState(false);
  const { products: loonPackProducts } = useLoonProducts({
    autoFetch: true,
  });
  const handlePortalError = useCallback(() => {
    toast.error('Stripe Customer Portal is unavailable right now.');
  }, []);
  const {
    portalUrl: billingPortalUrl,
    loadingPortal,
    openPortal,
  } = useUserStripePortal({
    returnUrl: '/library',
    autoFetch: menuOpen,
    trackingLabel: 'Stripe Customer Portal Visit',
    trackingContext: {
      link_location: 'library',
    },
    onError: handlePortalError,
  });

  const rapidReloadBadgeLabel = useMemo(() => {
    if (!user.rapidReload) {
      return null;
    }
    const pack = loonPackProducts.find(
      (p) => p.stripePriceId === user.rapidReload
    );
    const loons = pack != null ? getProductLoonsGranted(pack) : null;
    const count = loons != null && Number.isFinite(loons) ? String(loons) : '…';
    return `Enabled · ${count}`;
  }, [loonPackProducts, user.rapidReload]);

  const formattedBalance = useMemo(() => {
    const normalized = Number.isFinite(user.loonBalance)
      ? Math.max(0, Math.floor(user.loonBalance))
      : 0;
    return normalized.toLocaleString('en-US');
  }, [user.loonBalance]);

  const handlePortalClick = () => {
    if (loadingPortal) {
      return;
    }

    trackLoonMenuItemClick(
      'Stripe Customer Portal',
      'stripe_customer_portal',
      'external'
    );
    void openPortal();
  };

  const trackLoonMenuOpenChange = (nextOpen: boolean) => {
    analyticsService.trackNavigationMenuToggle(
      'library_loon_summary',
      nextOpen ? 'open' : 'closed',
      {
        surface: 'library_loons',
        link_location: 'library_sidebar',
        source_path: location.pathname,
        is_mobile: isMobile,
      }
    );
    setMenuOpen(nextOpen);
  };

  const trackLoonMenuItemClick = (
    label: string,
    target: string,
    linkType: 'internal' | 'external' = 'internal'
  ) => {
    analyticsService.trackSidebarItemClick(label, {
      surface: 'library_loons',
      link_location: 'library_sidebar',
      navigation_target: target,
      link_type: linkType,
      source_path: location.pathname,
      is_mobile: isMobile,
    });
  };

  const openTrackedDialog = (label: string, dialog: LibraryDialog) => {
    trackLoonMenuItemClick(label, dialog);
    openDialog(dialog);
  };

  const loonAvatar = (
    <Avatar>
      <AvatarImage src={ASSETS.loonLite} alt="" />
      <AvatarFallback>L</AvatarFallback>
    </Avatar>
  );

  const trigger = (
    <button
      type="button"
      title="Loon balance"
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-background px-3 py-3 text-left shadow-sm transition hover:bg-accent',
        collapsedDesktop && 'justify-center px-2'
      )}
    >
      {loonAvatar}
      {!collapsedDesktop && (
        <>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">
              <AnimatedOdometerNumber text={formattedBalance} />
            </div>
            <div className="text-xs text-muted-foreground">Loon balance</div>
          </div>
          {isMobile ? (
            <ChevronRightIcon
              className={cn(
                'text-muted-foreground transition-transform',
                menuOpen && 'rotate-90'
              )}
            />
          ) : (
            <ChevronsUpDownIcon className="text-muted-foreground" />
          )}
        </>
      )}
      {collapsedDesktop && <span className="sr-only">Loon balance</span>}
    </button>
  );

  if (isMobile) {
    return (
      <Collapsible
        open={menuOpen}
        onOpenChange={trackLoonMenuOpenChange}
        className="group/collapsible"
      >
        <CollapsibleTrigger asChild>{trigger}</CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="flex flex-col gap-1 rounded-xl border border-sidebar-border bg-background p-2 shadow-sm">
            <div className="px-2 py-2">
              <div className="flex items-center gap-3">
                {loonAvatar}
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {formattedBalance} Loons available
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Use them to play Airhead Games
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => openTrackedDialog('Buy Loons', 'buy-loons')}
            >
              <CoinsIcon />
              Buy Loons
            </Button>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() =>
                openTrackedDialog('Manage Rapid Reload', 'rapid-reload')
              }
            >
              <ZapIcon />
              Manage Rapid Reload
              {rapidReloadBadgeLabel ? (
                <Badge variant="secondary" className="ml-auto">
                  {rapidReloadBadgeLabel}
                </Badge>
              ) : null}
            </Button>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => openTrackedDialog('Loon History', 'loon-history')}
            >
              <HistoryIcon />
              Loon history
            </Button>
            <Button
              variant="ghost"
              className="justify-start"
              disabled={loadingPortal}
              onClick={handlePortalClick}
            >
              <ExternalLinkIcon />
              {loadingPortal ? 'Loading portal...' : 'Stripe customer portal'}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={trackLoonMenuOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={8}
        className="w-72"
      >
        <DropdownMenuLabel className="px-2 py-2">
          <div className="flex items-center gap-3">
            {loonAvatar}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {formattedBalance} Loons available
              </div>
              <div className="text-xs text-muted-foreground">
                Use them to play Airhead Games
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={librarySidebarDropdownItemClassName}
          onClick={() => openTrackedDialog('Buy Loons', 'buy-loons')}
        >
          <CoinsIcon />
          Buy Loons
        </DropdownMenuItem>
        <DropdownMenuItem
          className={librarySidebarDropdownItemClassName}
          onClick={() =>
            openTrackedDialog('Manage Rapid Reload', 'rapid-reload')
          }
        >
          <ZapIcon />
          Manage Rapid Reload
          {rapidReloadBadgeLabel ? (
            <Badge variant="secondary" className="ml-auto">
              {rapidReloadBadgeLabel}
            </Badge>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className={librarySidebarDropdownItemClassName}
          onClick={() => openTrackedDialog('Loon History', 'loon-history')}
        >
          <HistoryIcon />
          Loon history
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={librarySidebarDropdownItemClassName}
          disabled={!billingPortalUrl}
          onClick={handlePortalClick}
        >
          <ExternalLinkIcon />
          {loadingPortal ? 'Loading portal...' : 'Stripe customer portal'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LoonSummary;
